import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { requestEntryUrl } from "@/lib/requestEntry";
import { ArrowRight, Bike, BriefcaseBusiness, ChevronDown, GraduationCap, Hammer, Laptop, Lightbulb, Megaphone, Sprout, UsersRound } from "lucide-react";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import { CANONICAL_ORIGIN } from "@/lib/seo";
import "./Home.css";

// Keep the established SEO entry points and use real category slugs from the catalog.
const HOME_CATEGORIES = [
  { slug: "it-net", name: "IT & teknologi", path: "/hjaelp/it-teknologi", icon: Laptop },
  { slug: "markedsfoering", name: "Markedsføring", path: "/hjaelp/markedsfoering", icon: Megaphone },
  { slug: "ivaerksaetteri-virksomhed", name: "Iværksætteri & virksomhed", path: "/hjaelp/ivaerksaetteri-virksomhed", icon: BriefcaseBusiness },
  { slug: "cykler", name: "Cykler", path: "/kategorier/cykler", icon: Bike },
  { slug: "have-motor", name: "Have & havemaskiner", path: "/kategorier/have-motor", icon: Sprout },
  { slug: "toemrer", name: "Tømrer & snedker", path: "/kategorier/toemrer", icon: Hammer },
];
const STEPS = [
  ["Beskriv dit problem", "Fortæl hvad du har brug for hjælp til. Del gerne billeder og lidt om det du allerede har prøvet."],
  ["Vælg din hjælper", "Se profiler og prisforslag. Aftal pris, omfang og tidspunkt før du går videre."],
  ["Få hjælp via video", "Tal sammen, stil spørgsmål og få personlig vejledning. I kan også bruge chat og dele billeder."],
];
const BENEFITS = [
  [Lightbulb, "Hjælp til dit konkrete problem", "Tag udgangspunkt i det du sidder fast med og få hjælp til næste skridt."],
  [UsersRound, "Tal med et menneske", "Få personlig sparring og stil alle de spørgsmål du har brug for."],
  [GraduationCap, "Lær noget du kan bruge igen", "Bliv klogere undervejs og tag din nye viden med videre."],
];
const FAQS = [
  ["Hvordan fungerer SpørgMig?", "Du beskriver dit problem og modtager prisforslag fra relevante eksperter. Du vælger selv hvem du vil have hjælp af og aftaler pris, omfang og tidspunkt. Derefter får du personlig hjælp via chat, billeder eller video."],
  ["Hvad kan jeg få hjælp til?", "Du kan få vejledning inden for blandt andet IT, markedsføring, iværksætteri, cykler og gør det selv. Se kategorierne eller beskriv dit problem så en relevant ekspert kan vurdere om de kan hjælpe."],
  ["Hvordan foregår hjælpen via video?", "Videosamtalen åbnes fra din aftale på SpørgMig når betalingen er bekræftet. Du bruger din browser og giver adgang til kamera og mikrofon. Undervejs kan du vise problemet og stille spørgsmål."],
  ["Hvad koster det at få hjælp?", "Prisen afhænger af opgaven og den ekspert du vælger. Du modtager et prisforslag og ser pris og omfang før du accepterer. Du betaler først efter, at du har accepteret et tilbud."],
];
const SCHEMAS = [
  { "@context": "https://schema.org", "@type": "Organization", name: "SpørgMig", url: CANONICAL_ORIGIN, logo: `${CANONICAL_ORIGIN}/sporgmig-logo.png` },
  { "@context": "https://schema.org", "@type": "WebSite", name: "SpørgMig", url: CANONICAL_ORIGIN, inLanguage: "da-DK" },
];

function Photo({ name, alt, className = "", eager = false, sizes = "(max-width: 767px) 100vw, 50vw" }) {
  return <img className={className} src={`/images/home/${name}-1280.webp`} srcSet={`/images/home/${name}-640.webp 640w, /images/home/${name}-1280.webp 1280w`} sizes={sizes} width="1280" height="960" alt={alt} loading={eager ? "eager" : "lazy"} fetchPriority={eager ? "high" : "auto"} decoding="async" />;
}

function Accent({ className = "" }) {
  return <svg className={`home-accent ${className}`} viewBox="0 0 72 64" fill="none" aria-hidden="true"><path d="M12 47 3 41M28 29 15 11M48 24 49 3" stroke="currentColor" strokeWidth="4" strokeLinecap="round" /></svg>;
}

export default function Home() {
  const [categories, setCategories] = useState([]);
  const navigate = useNavigate();
  const { hash, search } = useLocation();
  useEffect(() => {
    if (hash === "#saadan-virker-det") document.getElementById("saadan-virker-det")?.scrollIntoView();
  }, [hash]);
  useEffect(() => {
    let active = true;
    api.get("/categories").then(({ data }) => { if (active && Array.isArray(data)) setCategories(data); }).catch(() => {});
    return () => { active = false; };
  }, []);

  const startExample = (problem) => {
    try { sessionStorage.setItem("en_draft_problem", problem); } catch { /* The request flow remains accessible without a saved draft. */ }
    navigate(requestEntryUrl(search));
  };

  return <>
    <Seo title="SpørgMig – Få hjælp fra nogen, der faktisk ved det" description="Få personlig hjælp fra et menneske med den rette viden. Beskriv dit problem og få vejledning via chat, billeder eller video." path="/" schemas={SCHEMAS} />
    <div className="home-page">
      <section className="home-container home-hero" aria-labelledby="home-title">
        <div className="home-hero-copy">
          <h1 id="home-title">Kom videre med hjælp fra <span>én der ved hvordan.</span></h1>
          <p>Få personlig hjælp via video. Beskriv dit problem og find en person med den viden du mangler.</p>
          <div className="home-actions">
            <Link to={requestEntryUrl(search)} className="home-button" data-testid="home-create-request">Opret forespørgsel <ArrowRight size={18} aria-hidden="true" /></Link>
            <Link to="/eksperter" className="home-text-link">Find hjælp <ArrowRight size={16} aria-hidden="true" /></Link>
          </div>
          <div className="mt-3 text-sm leading-6 text-brand-muted">Gratis at sende · Du vælger selv, om du vil acceptere et tilbud.</div>
          <div className="home-handwritten" aria-hidden="true">
            <svg viewBox="0 0 58 53" fill="none"><path d="M53 46C18 49 15 24 17 6m0 0L7 17M17 6l8 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
            <span>Gode råd er tættere på<br />end du tror.</span>
          </div>
        </div>
        <div className="home-hero-visual">
          <img
            src="/images/home/problem-solving-1254.webp"
            srcSet="/images/home/problem-solving-640.webp 640w, /images/home/problem-solving-1254.webp 1254w"
            sizes="(max-width: 767px) calc(100vw - 32px), (max-width: 1240px) 50vw, 617px"
            width="1254"
            height="1254"
            alt="Illustration af en mand der tilslutter sin router med vejledning fra en ekspert via video"
            loading="eager"
            fetchPriority="high"
            decoding="async"
          />
        </div>
      </section>

      <section className="home-container home-categories" aria-labelledby="home-categories-title">
        <div className="home-section-heading"><h2 id="home-categories-title">Hvad har du brug for hjælp til?</h2><p>Find den viden der kan hjælpe dig videre.</p></div>
        <div className="home-category-grid">
          {HOME_CATEGORIES.map(({ slug, name, path, icon: Icon }) => <Link key={slug} to={path} className="home-category-card"><Icon size={31} strokeWidth={1.5} aria-hidden="true" /><span>{categories.find((category) => category.slug === slug)?.name || name}</span><ArrowRight size={17} aria-hidden="true" /></Link>)}
        </div>
        <Link to="/kategorier" className="home-text-link home-all-categories">Se alle kategorier <ArrowRight size={16} aria-hidden="true" /></Link>
      </section>

      <section id="saadan-virker-det" className="home-container home-how" aria-labelledby="home-how-title">
        <div className="home-how-copy"><h2 id="home-how-title">Fra spørgsmål til løsning</h2><p>Personlig hjælp. Ét skridt ad gangen.</p>
          <ol className="home-steps">{STEPS.map(([title, body], index) => <li key={title}><span className="home-step-number" aria-hidden="true">{index + 1}</span><div><h3>{title}</h3><p>{body}</p></div></li>)}</ol>
        </div>
        <div className="home-examples">
          <span className="home-example-label">Eksempler på spørgsmål</span><Accent />
          <button type="button" className="home-example-card" onClick={() => startExample("Min computer er blevet langsom")}>
            <span className="home-example-icon"><Laptop size={38} strokeWidth={1.4} aria-hidden="true" /></span><span className="home-example-body"><strong>Min computer er<br />blevet langsom</strong><span className="home-tags"><span>IT & teknologi</span><span>Computer</span></span></span><ArrowRight size={18} aria-hidden="true" />
          </button>
          <button type="button" className="home-example-card" onClick={() => startExample("Hvordan kommer jeg i gang med annoncer?")}>
            <span className="home-example-icon"><Megaphone size={38} strokeWidth={1.4} aria-hidden="true" /></span><span className="home-example-body"><strong>Hvordan kommer jeg<br />i gang med annoncer?</strong><span className="home-tags"><span>Markedsføring</span><span>Sociale medier</span></span></span><ArrowRight size={18} aria-hidden="true" />
          </button>
          <p className="home-example-hint">Klik på et eksempel og beskriv din egen situation.</p>
        </div>
      </section>

      <section className="home-container home-benefits" aria-labelledby="home-benefits-title">
        <div className="home-benefit-photo"><Photo name="learn-together" alt="Illustration af en kvinde der får vejledning ved sin bærbare computer" /></div>
        <div className="home-benefits-copy"><h2 id="home-benefits-title">Du behøver ikke<br />kunne det hele selv.</h2><p>Nogle gange er det rart at tale med én der har prøvet det før. På SpørgMig kan du få hjælp til at komme videre.</p>
          <ul>{BENEFITS.map(([Icon, title, body]) => <li key={title}><span className="home-benefit-icon"><Icon size={25} strokeWidth={1.6} aria-hidden="true" /></span><div><h3>{title}</h3><p>{body}</p></div></li>)}</ul>
        </div>
      </section>

      <section className="home-container home-recruitment" aria-labelledby="home-recruitment-title">
        <div className="home-recruitment-copy"><h2 id="home-recruitment-title">Din viden kan hjælpe andre.</h2><p>Del det du er god til og tjen på at hjælpe andre videre.</p><Link to="/tjen-penge-paa-din-viden" className="home-button home-button-light">Tjen på din viden <ArrowRight size={17} aria-hidden="true" /></Link></div>
        <div className="home-recruitment-photo"><Photo name="share-knowledge" alt="Illustration af en hjælper med en tablet" /><Accent /></div>
      </section>

      <section className="home-container home-faq" aria-labelledby="home-faq-title">
        <div className="home-section-heading"><h2 id="home-faq-title">Spørgsmål og svar</h2><p>Her får du svar på nogle af de mest almindelige spørgsmål.</p></div>
        <div className="home-faq-list">{FAQS.map(([question, answer]) => <details key={question}><summary>{question}<ChevronDown size={19} aria-hidden="true" /></summary><p>{answer}</p></details>)}</div>
        <Link to="/hjaelp" className="home-text-link">Se flere spørgsmål og svar <ArrowRight size={16} aria-hidden="true" /></Link>
      </section>
    </div>
  </>;
}
